import * as THREE from 'three';
import { BOAT_DIMENSIONS, CREW_CONFIG, ANIMATION_CONFIG } from './constants';

// =============================================================================
// CREW POSITIONING UTILITIES
// =============================================================================

/**
 * Calculates the positions of all crew members (8 rowers + coxswain) along the hull
 *
 * @returns Object containing crew start position, seat spacing, and coxswain position
 */
export function calculateCrewPositions() {
  const { HULL_LENGTH, BOW_TAPER_START, STERN_TAPER_START, STROKE_SEAT_OFFSET } = BOAT_DIMENSIONS;
  const { NUM_ROWERS, COXSWAIN_POSITION_MULTIPLIER } = CREW_CONFIG;

  // Position seat 1 at bow taper (starts at 15% from bow end)
  const bowTaperStart = HULL_LENGTH * BOW_TAPER_START;
  const sternTaperStart = HULL_LENGTH * STERN_TAPER_START;

  // Seat 1 position: just at the bow taper
  const seat1X = -HULL_LENGTH / 2 + bowTaperStart;

  // Stroke seat (8) position: just before stern taper
  const seat8X = -HULL_LENGTH / 2 + sternTaperStart - STROKE_SEAT_OFFSET;

  // Calculate spacing to fit 8 rowers between these positions
  const availableLength = seat8X - seat1X;
  const seatSpacing = availableLength / (NUM_ROWERS - 1); // 7 gaps between 8 rowers

  // Start position for first rower (seat 1 at bow taper)
  const crewStartX = seat1X;

  // Coxswain position (in stern taper area, behind stroke)
  const coxswainX = seat8X + seatSpacing * COXSWAIN_POSITION_MULTIPLIER;

  return {
    crewStartX,
    seatSpacing,
    coxswainX,
    seat1X,
    seat8X,
    availableLength,
  };
}

/**
 * Calculates the X position for a specific seat number (1-8)
 *
 * @param seatNumber - The seat number (1 = bow, 8 = stroke)
 * @returns The X position along the hull
 */
export function getSeatPosition(seatNumber: number): number {
  if (seatNumber < 1 || seatNumber > 8) {
    throw new Error('Seat number must be between 1 and 8');
  }

  const { crewStartX, seatSpacing } = calculateCrewPositions();
  return crewStartX + (seatNumber - 1) * seatSpacing;
}

/**
 * Determines if a seat is on the port side (even seats) or starboard side (odd seats)
 * Standard rigging: bow seat (1) is starboard, alternates from there
 *
 * @param seatNumber - The seat number (1-8)
 * @returns true if port side (even), false if starboard side (odd)
 */
export function isPortSide(seatNumber: number): boolean {
  return seatNumber % 2 === 0;
}

// =============================================================================
// ANIMATION UTILITIES
// =============================================================================

/**
 * Calculates the current animation phase and progress for rowing motion
 *
 * @param time - Current elapsed time in seconds
 * @param strokeDuration - Optional custom stroke duration (defaults to config value)
 * @returns Object with phase info and interpolated values
 */
export function calculateRowingAnimation(time: number, strokeDuration?: number) {
  const {
    STROKE_DURATION,
    DRIVE_PHASE_RATIO,
    OAR_CATCH_ANGLE,
    OAR_FINISH_ANGLE,
    BODY_CATCH_ANGLE,
    BODY_FINISH_ANGLE,
  } = ANIMATION_CONFIG;

  const duration = strokeDuration ?? STROKE_DURATION;

  // Normalize time to 0-1 range within stroke cycle
  const t = (time % duration) / duration;

  let angle: number;
  let bodyLean: number;
  let bladeHeight: number; // Vertical offset for blade (elliptical path)
  let phase: 'drive' | 'recovery';
  let phaseProgress: number;

  if (t < DRIVE_PHASE_RATIO) {
    // Drive phase: fast pull through water
    phase = 'drive';
    phaseProgress = t / DRIVE_PHASE_RATIO;
    angle = THREE.MathUtils.lerp(OAR_CATCH_ANGLE, OAR_FINISH_ANGLE, phaseProgress);
    bodyLean = THREE.MathUtils.lerp(BODY_CATCH_ANGLE, BODY_FINISH_ANGLE, phaseProgress);

    // Blade stays at/below water level during drive (slight dip for realistic water entry)
    bladeHeight = -0.05; // 5cm below waterline during drive
  } else {
    // Recovery phase: slower return
    phase = 'recovery';
    phaseProgress = (t - DRIVE_PHASE_RATIO) / (1 - DRIVE_PHASE_RATIO);
    angle = THREE.MathUtils.lerp(OAR_FINISH_ANGLE, OAR_CATCH_ANGLE, phaseProgress);
    bodyLean = THREE.MathUtils.lerp(BODY_FINISH_ANGLE, BODY_CATCH_ANGLE, phaseProgress);

    // Blade follows elliptical path during recovery:
    // - Rises from water (finish)
    // - Travels forward in air
    // - Drops back to water (catch)
    // Use sine wave for smooth up-down motion
    const recoveryHeight = Math.sin(phaseProgress * Math.PI); // 0 → 1 → 0
    bladeHeight = recoveryHeight * 0.25; // Peak height: 25cm above water
  }

  return {
    angle, // Oar angle in degrees (horizontal sweep)
    bodyLean, // Body lean angle in degrees
    bladeHeight, // Vertical offset in meters (elliptical path)
    phase, // 'drive' or 'recovery'
    phaseProgress, // 0-1 progress within current phase
    cycleProgress: t, // 0-1 progress through entire stroke cycle
  };
}

/**
 * Calculates rowing animation with individual rower timing offset
 * This allows for staggered/sequential stroke timing across the boat
 *
 * @param time - Current elapsed time in seconds
 * @param seatNumber - Seat number (1-8)
 * @param offsetPerSeat - Time offset between seats in seconds (default: 0 for synchronized)
 * @param strokeDuration - Optional custom stroke duration (defaults to config value)
 * @returns Animation values with timing offset applied
 */
export function calculateRowingAnimationWithOffset(
  time: number,
  seatNumber: number,
  offsetPerSeat: number = 0,
  strokeDuration?: number
) {
  // Apply timing offset: stroke seat (8) leads, bow seat (1) follows
  // Seats are numbered 1-8, so seat 8 has offset 0, seat 7 has offset 1x, etc.
  const seatOffset = (8 - seatNumber) * offsetPerSeat;
  const offsetTime = time - seatOffset;

  return calculateRowingAnimation(offsetTime, strokeDuration);
}

// =============================================================================
// HULL GEOMETRY UTILITIES
// =============================================================================

/**
 * Calculates the width scale factor for hull taper at a given position
 *
 * @param t - Normalized position along hull (0 = bow, 1 = stern)
 * @returns Width scale factor (0-1)
 */
export function calculateHullWidthTaper(t: number): number {
  const { BOW_TAPER_START, STERN_TAPER_START } = BOAT_DIMENSIONS;

  if (t < BOW_TAPER_START) {
    return t / BOW_TAPER_START; // Bow taper
  } else if (t > STERN_TAPER_START) {
    return (1.0 - t) / (1.0 - STERN_TAPER_START); // Stern taper
  }
  return 1.0; // Full width in middle section
}

/**
 * Calculates the height taper factor for hull bottom at a given position
 *
 * @param t - Normalized position along hull (0 = bow, 1 = stern)
 * @returns Height taper factor (0-1)
 */
export function calculateHullHeightTaper(t: number): number {
  const { BOW_TAPER_START, STERN_TAPER_START } = BOAT_DIMENSIONS;

  if (t < BOW_TAPER_START) {
    return t / BOW_TAPER_START; // Bow taper
  } else if (t > STERN_TAPER_START) {
    return (1.0 - t) / (1.0 - STERN_TAPER_START); // Stern taper
  }
  return 1.0; // Full height in middle section
}

// =============================================================================
// MEMORY MANAGEMENT / DISPOSAL UTILITIES
// =============================================================================

/**
 * Safely disposes of a Three.js mesh and its geometry and material
 * Call this when removing objects from the scene to prevent memory leaks
 *
 * @param mesh - The mesh to dispose
 */
export function disposeMesh(mesh: THREE.Mesh) {
  if (!mesh) return;

  // Dispose geometry
  if (mesh.geometry) {
    mesh.geometry.dispose();
  }

  // Dispose material(s)
  if (mesh.material) {
    if (Array.isArray(mesh.material)) {
      mesh.material.forEach((material) => disposeMaterial(material));
    } else {
      disposeMaterial(mesh.material);
    }
  }
}

/**
 * Safely disposes of a Three.js material and its textures
 *
 * @param material - The material to dispose
 */
export function disposeMaterial(material: THREE.Material) {
  if (!material) return;

  // Dispose all textures
  Object.keys(material).forEach((key) => {
    const value = (material as any)[key];
    if (value && typeof value === 'object' && 'isTexture' in value) {
      value.dispose();
    }
  });

  material.dispose();
}

/**
 * Recursively disposes all meshes in a Three.js Object3D (group or scene)
 *
 * @param object - The object to dispose (will traverse children)
 */
export function disposeObject3D(object: THREE.Object3D) {
  if (!object) return;

  // Traverse and dispose children
  object.traverse((child) => {
    if ((child as THREE.Mesh).isMesh) {
      disposeMesh(child as THREE.Mesh);
    }
  });

  // Remove from parent
  if (object.parent) {
    object.parent.remove(object);
  }
}

// =============================================================================
// VECTOR/MATH UTILITIES
// =============================================================================

/**
 * Reusable Vector3 instances to avoid allocation in animation loops
 * Use these for temporary calculations instead of creating new Vector3 objects
 */
export const tempVec3 = new THREE.Vector3();
export const tempVec3_2 = new THREE.Vector3();
export const tempEuler = new THREE.Euler();
export const tempQuaternion = new THREE.Quaternion();

/**
 * Converts degrees to radians (convenience wrapper)
 *
 * @param degrees - Angle in degrees
 * @returns Angle in radians
 */
export function degToRad(degrees: number): number {
  return THREE.MathUtils.degToRad(degrees);
}

/**
 * Converts radians to degrees (convenience wrapper)
 *
 * @param radians - Angle in radians
 * @returns Angle in degrees
 */
export function radToDeg(radians: number): number {
  return THREE.MathUtils.radToDeg(radians);
}

/**
 * Clamps a value between min and max
 *
 * @param value - Value to clamp
 * @param min - Minimum value
 * @param max - Maximum value
 * @returns Clamped value
 */
export function clamp(value: number, min: number, max: number): number {
  return THREE.MathUtils.clamp(value, min, max);
}

/**
 * Linear interpolation between two values
 *
 * @param start - Start value
 * @param end - End value
 * @param t - Interpolation factor (0-1)
 * @returns Interpolated value
 */
export function lerp(start: number, end: number, t: number): number {
  return THREE.MathUtils.lerp(start, end, t);
}
